import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { PaxInfo } from '@cart-js/core';
import { selectBehaviorFromBootstrap, selectCmsFromBootstrap, selectExperimentsFromBootstrap, selectIsLoggedIn, selectIsWebViewFromBootstrap } from 'store/slice/bootstrap';
import {
    selectAvailabilitySelectedDate,
    selectAvailabilitySelectedOfferDetail,
    selectFinalOfferOption,
    selectIsOfferDateChipSelected,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferSelected,
    selectPaxInfo,
    selectPossibleOfferOptions,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { ActivitiesBehavior } from 'core/type/behaviors.types';
import { SelectedOfferState } from 'component/page/detail/store/availabilityIndex.types';
import { setOfferNotification } from 'component/page/detail/store/availabilityIndex';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { getDateWithTimeInYmdFormat } from 'core-framework/util/datetime/date';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

interface StateProps {
    cms: CmsValue;
    behavior: ActivitiesBehavior;
    isWebView: boolean;
    selectedOffer: SelectedOfferState;
    selectedOfferId: number;
    offerTitle: string;
    isShowDateChipNotification: boolean;
    isShowOfferNotification: boolean;
    finalOfferOption?: EnrichedOfferOption;
    travelDate: string;
    paxInfo: PaxInfo[];
    expandFirstOfferOnDetailsLanding: boolean;
    isLoggedIn: boolean;
}

interface DispatchProps {
    setOfferNotification: (offerNotification: Record<string, boolean>) => void;
}

interface OwnProps {
    isAddToCartEnabled: boolean;
    disabled: boolean;
    buttonWidth?: 'parent-fit' | 'content-fit';
}

export type Props = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectBehaviorFromBootstrap,
    selectIsWebViewFromBootstrap,
    selectOfferSelected,
    selectAvailabilitySelectedOfferDetail,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectIsOfferDateChipSelected,
    selectPossibleOfferOptions,
    selectFinalOfferOption,
    selectAvailabilitySelectedDate,
    selectPaxInfo,
    selectIsLoggedIn,
    selectExperimentsFromBootstrap,
    (
        cms,
        behavior,
        isWebView,
        selectedOffer,
        selectedOfferDetail,
        selectedOfferId,
        isOfferDateChipSelected,
        possibleOfferOptions,
        finalOfferOption,
        availabilitySelectedDate,
        paxInfo,
        isLoggedIn,
    ): StateProps => {
        const offerTitle = selectedOfferDetail?.title || '';
        const isAllOfferSpecSelected = possibleOfferOptions.length === 1;
        const isShowDateChipNotification = !isOfferDateChipSelected;
        const isShowOfferNotification = !isAllOfferSpecSelected;
        const expandFirstOfferOnDetailsLanding = isOn(FeatureName.ExpandFirstOfferOnDetailsLanding);

        return {
            cms,
            behavior,
            isWebView,
            selectedOffer,
            selectedOfferId,
            offerTitle,
            isShowDateChipNotification,
            isShowOfferNotification,
            finalOfferOption,
            paxInfo,
            travelDate: getDateWithTimeInYmdFormat(availabilitySelectedDate),
            expandFirstOfferOnDetailsLanding,
            isLoggedIn,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setOfferNotification: (offerNotification: Record<string, boolean>) => dispatch(setOfferNotification(offerNotification)),
});
